import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { INDUSTRIES, getIndustryLabel } from '@/config/industries';
import type { IndustryId } from '@/config/industries';
import { deleteCompanyComplete } from '@/lib/companyCascadeDelete';
import { Loader2 } from 'lucide-react';

export function OwnerCompanyDetailPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<{ name: string; industry?: string } | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [industryId, setIndustryId] = useState<IndustryId | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId)).then((snap) => {
      if (!snap.exists()) {
        setCompany(null);
        return;
      }
      const d = snap.data();
      const nameVal = d.name ?? '';
      const industryVal = d.industry ?? '';
      setCompany({ name: nameVal, industry: industryVal });
      setName(nameVal);
      setIndustryId(INDUSTRIES.some((i) => i.id === industryVal) ? (industryVal as IndustryId) : '');
    });
  }, [companyId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;
    setError('');
    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (!industryId) {
      setError('Selecciona un rubro empresarial');
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        name: name.trim(),
        industry: industryId,
        updatedAt: serverTimestamp(),
      });
      setCompany((c) => (c ? { ...c, name: name.trim(), industry: industryId } : null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    if (!open && isDeleting) return;
    setDeleteDialogOpen(open);
    if (!open) setDeleteError('');
  };

  const handleConfirmDeleteCompany = async () => {
    if (!companyId) return;
    setDeleteError('');
    setIsDeleting(true);
    try {
      await deleteCompanyComplete(companyId);
      setDeleteDialogOpen(false);
      navigate('/owner/companies', { replace: true });
    } catch (err) {
      console.error('[OwnerCompanyDetail] Error al eliminar empresa:', err);
      setDeleteError(err instanceof Error ? err.message : 'No se pudo eliminar la empresa');
    } finally {
      setIsDeleting(false);
    }
  };

  if (company === undefined) return <p className="text-muted-foreground">Cargando...</p>;
  if (!companyId || company === null) return <p className="text-muted-foreground">Empresa no encontrada.</p>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/owner/companies">← Empresas</Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Editar empresa</CardTitle>
          <p className="text-sm text-muted-foreground">ID: {companyId}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="companyName">Nombre</Label>
              <Input
                id="companyName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre de la empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyIndustry">Rubro empresarial</Label>
              <select
                id="companyIndustry"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={industryId}
                onChange={(e) => setIndustryId(e.target.value as IndustryId | '')}
              >
                <option value="">Selecciona un rubro</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
              {company.industry && !industryId && (
                <p className="text-xs text-muted-foreground">Actual: {getIndustryLabel(company.industry)}</p>
              )}
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-destructive">Zona de peligro</CardTitle>
          <p className="text-sm text-muted-foreground">
            Eliminar la empresa de forma permanente. No hay forma de recuperar los datos.
          </p>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            Eliminar empresa
          </Button>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={handleDeleteDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar empresa</DialogTitle>
            <DialogDescription>
              Esta acción es irreversible. Se eliminarán permanentemente todos los empleados,
              evaluaciones, campañas y resultados asociados a esta empresa.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{deleteError}</div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={isDeleting} onClick={() => handleDeleteDialogOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" variant="destructive" disabled={isDeleting} onClick={handleConfirmDeleteCompany}>
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Eliminando...
                </>
              ) : (
                'Confirmar eliminación'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
